import { Alert, Box, Skeleton } from '@mui/material'
import { FC } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { DateTime } from 'luxon'
import { CompanyInstagramInsightTooltip } from './CompanyInstagramInsightTooltip'

export type CompanyInstagramInsgihtChartData = {
  followerCount: number
  value: number
  date: string // YYYY/MM/DD
}[]

type Props = {
  data: CompanyInstagramInsgihtChartData
  yAxisLabel: string
  yAxisUnit: string
  loading?: boolean
  startFromDataMin?: boolean
  showFollowerCount: boolean
  showProfileViews: boolean
  /** 連携開始日（yyyy/MM/dd形式）。データ内に存在する場合、グラフ上に参照線を表示する */
  connectedAt?: string
}

export const CompanyInstagramInsightChart: FC<Props> = ({
  data,
  yAxisLabel,
  yAxisUnit,
  loading,
  showFollowerCount,
  showProfileViews,
  connectedAt,
}) => {
  if (loading) return <Skeleton variant="rectangular" height={300} />
  if (!data.length) {
    return <Alert severity="warning">インサイト情報が存在しません。</Alert>
  }

  const xAxisInterval =
    data.length > 180 ? Math.floor(data.length / 12) :
    data.length > 60  ? Math.floor(data.length / 8) :
    0

  // 最終日が interval の倍数でない場合も必ず表示するよう明示的に ticks を生成
  const xAxisTicks = (() => {
    const step = xAxisInterval > 0 ? xAxisInterval + 1 : 1
    const dates: string[] = []
    for (let i = 0; i < data.length; i += step) {
      dates.push(data[i]!.date)
    }
    const lastDate = data[data.length - 1]!.date
    if (dates[dates.length - 1] !== lastDate) dates.push(lastDate)
    return dates
  })()

  const connectedAtIndex = connectedAt ? data.findIndex((d) => d.date === connectedAt) : -1
  const showConnectedAtLine = connectedAtIndex !== -1
  const connectedAtLabelPosition = connectedAtIndex < data.length / 2 ? 'insideTopRight' : 'insideTopLeft'

  const domains = ([dataMin, dataMax]: [number, number]): [number, number] => {
    const max = dataMax || 0
    const min = dataMin || 0
    const range = max - min
    let padding
    if (range < 100) {
      padding = 50 // 小さい範囲の場合は±50
    } else if (range < 1000) {
      padding = 100 // 中程度の範囲の場合は±100
    } else {
      padding = Math.ceil(range * 0.1) // 大きい範囲の場合は範囲の10%
    }

    return [Math.max(0, min - padding), max + padding] as [number, number]
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ right: 15 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          {showConnectedAtLine && (
            <ReferenceLine
              x={connectedAt}
              yAxisId="left"
              stroke="#ed6c02"
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{ value: '連携開始日', position: connectedAtLabelPosition, fontSize: 11, fill: '#b54708', fontWeight: 'bold' }}
            />
          )}
          <XAxis
            dataKey="date"
            ticks={xAxisTicks}
            tickFormatter={(d) => DateTime.fromFormat(d, 'yyyy/MM/dd').toFormat('M/d')}
            tick={{ fontSize: '12px' }}
          />
          <YAxis
            yAxisId="left"
            dataKey="value"
            tick={({ x, y, payload, index, visibleTicksCount }: any) => {
              if (index === visibleTicksCount - 1) return <g />
              return (
                <text x={x} y={y} dy="0.355em" textAnchor="end" fontSize={12} fill="#666">
                  {Number(payload.value).toLocaleString()}
                </text>
              )
            }}
            allowDecimals={false}
            domain={domains}
          >
            <Label angle={-90} dx={-15} fontSize={12} style={{ fill: '#585858' }}>
              {`${yAxisLabel}（${yAxisUnit}）`}
            </Label>
          </YAxis>
          <YAxis
            hide={!showFollowerCount}
            yAxisId="right"
            dataKey="followerCount"
            orientation="right"
            tick={({ x, y, payload, index, visibleTicksCount }: any) => {
              if (index === visibleTicksCount - 1) return <g />
              return (
                <text x={x} y={y} dy="0.355em" textAnchor="start" fontSize={12} fill="#666">
                  {Number(payload.value).toLocaleString()}
                </text>
              )
            }}
            allowDecimals={false}
            domain={domains}
          >
            <Label angle={90} dx={15} fontSize={12} style={{ fill: '#585858' }}>
              フォロワー数（人）
            </Label>
          </YAxis>
          <Tooltip
            content={(props) => (
              <CompanyInstagramInsightTooltip
                props={props}
                data={data}
                yAxisLabel={yAxisLabel}
                showProfileViews={showProfileViews}
                showFollowerCount={showFollowerCount}
              />
            )}
          />

          <Line
            hide={!showFollowerCount}
            dataKey="followerCount"
            isAnimationActive={false}
            fill={'#1976d2'}
            strokeWidth={2.5}
            yAxisId="right"
            dot={{ fill: '#1976d2', strokeWidth: 2 }}
          />
          <Line
            hide={!showProfileViews}
            dataKey="value"
            isAnimationActive={false}
            stroke="#ff9800"
            strokeWidth={2.5}
            yAxisId="left"
            dot={{ fill: '#ff9800', strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}

export const DummyCompanyInstagramInsightChart = (props: { yAxisLabel: string; yAxisUnit: string }) => {
  return (
    <CompanyInstagramInsightChart
      data={getDummyCompanyInstagramInsightChartData(14)}
      showFollowerCount
      showProfileViews
      {...props}
    />
  )
}

export const getDummyCompanyInstagramInsightChartData = (days: number) =>
  Array(days)
    .fill(0)
    .map((_, i) => {
      const v = days - i
      const down = v % 4 === 0
      return {
        date: DateTime.fromFormat('2022/10/10', 'yyyy/MM/dd')
          .minus({ day: i + 1 })
          .toFormat('yyyy/MM/dd'),
        value: v * (down ? 5 : 10),
        followerCount: v * (down ? 1 : 3),
      }
    })
    .reverse()
